import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';

export function NotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col items-center overflow-x-clip bg-stitch-canvas px-4 py-12 text-center sm:px-6 lg:px-8">
      <SEO title="Page not found | InterviewR" noindex />
      {/* Illustration */}
      <div className="relative mb-12 flex w-full max-w-2xl justify-center">
        <div className="absolute -left-12 -top-12 h-48 w-48 rounded-full bg-stitch-primary-soft/80 blur-3xl" />
        <div className="absolute -bottom-12 -right-12 h-64 w-64 rounded-full bg-stitch-accent-soft/80 blur-3xl" />
        <div className="relative flex aspect-square w-full max-w-md flex-col items-center justify-center overflow-hidden rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-modal">
          <div className="relative z-10 space-y-6">
            <div className="flex justify-center">
              <span
                className="material-symbols-outlined text-7xl text-stitch-primary md:text-8xl"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                search_off
              </span>
            </div>
            <div className="space-y-2">
              <div className="text-6xl font-extrabold tracking-tighter text-stitch-primary/20 md:text-7xl">404</div>
              <div className="mx-auto h-1.5 w-12 rounded-full bg-stitch-primary" />
            </div>
          </div>
          <div className="pointer-events-none absolute inset-0 opacity-10">
            <div className="absolute left-4 top-4 h-12 w-12 rotate-12 rounded-stitch-lg border-2 border-stitch-primary" />
            <div className="absolute bottom-10 right-8 h-16 w-16 rounded-full border-2 border-stitch-accent" />
            <div className="absolute left-8 top-1/2 h-4 w-4 rounded-full bg-stitch-primary" />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-xl space-y-6">
        <h1 className="text-4xl font-semibold tracking-tight text-stitch-fg md:text-5xl">
          {t('notFound.title', 'Page not found')}
        </h1>
        <p className="text-lg leading-relaxed text-stitch-muted">
          {t(
            'notFound.description',
            "Sorry, we couldn't find the page you're looking for. Let's get you back on track.",
          )}
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <Link
            to="/"
            className="flex w-full items-center justify-center gap-2 rounded-stitch-xl bg-stitch-primary px-8 py-3.5 font-semibold text-white shadow-lg shadow-stitch-primary/25 transition-all hover:bg-stitch-primary-hover active:scale-[0.98] sm:w-auto"
          >
            <span className="material-symbols-outlined text-[20px]">home</span>
            {t('notFound.home', 'Go home')}
          </Link>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex w-full items-center justify-center gap-2 rounded-stitch-xl border border-stitch-border bg-stitch-surface px-8 py-3.5 font-semibold text-stitch-fg transition-all hover:bg-stitch-surface-2 active:scale-[0.98] sm:w-auto"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            {t('notFound.back', 'Go back')}
          </button>
        </div>
      </div>

      {/* Helpful links */}
      <div className="mt-16 grid w-full max-w-5xl grid-cols-1 gap-6 text-left md:grid-cols-3">
        <Link
          to="/about"
          className="group rounded-2xl border border-stitch-border bg-stitch-surface p-6 transition-all hover:border-stitch-primary hover:shadow-xl hover:shadow-stitch-primary/5"
        >
          <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft transition-colors group-hover:bg-stitch-primary group-hover:text-white">
            <span className="material-symbols-outlined text-stitch-primary group-hover:text-white">help</span>
          </div>
          <h3 className="mb-1 font-semibold text-stitch-fg">{t('notFound.helpAbout', 'About InterviewR')}</h3>
          <p className="text-sm text-stitch-muted">{t('notFound.helpAboutDesc', 'Learn our story and mission')}</p>
        </Link>
        <Link
          to="/try-interview"
          className="group rounded-2xl border border-stitch-border bg-stitch-surface p-6 transition-all hover:border-stitch-primary hover:shadow-xl hover:shadow-stitch-primary/5"
        >
          <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft transition-colors group-hover:bg-stitch-primary group-hover:text-white">
            <span className="material-symbols-outlined text-stitch-primary group-hover:text-white">psychology</span>
          </div>
          <h3 className="mb-1 font-semibold text-stitch-fg">{t('notFound.helpAi', 'AI Practice')}</h3>
          <p className="text-sm text-stitch-muted">{t('notFound.helpAiDesc', 'Jump into a mock interview session')}</p>
        </Link>
        <Link
          to="/interviewers"
          className="group rounded-2xl border border-stitch-border bg-stitch-surface p-6 transition-all hover:border-stitch-primary hover:shadow-xl hover:shadow-stitch-primary/5"
        >
          <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft transition-colors group-hover:bg-stitch-primary group-hover:text-white">
            <span
              className="material-symbols-outlined text-stitch-primary group-hover:text-white"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              stars
            </span>
          </div>
          <h3 className="mb-1 font-semibold text-stitch-fg">{t('notFound.helpExperts', 'Browse Experts')}</h3>
          <p className="text-sm text-stitch-muted">{t('notFound.helpExpertsDesc', 'Find a mentor for your path')}</p>
        </Link>
      </div>
    </div>
  );
}
